<template>
    <div class="chatbot">
        <!-- Floating Action Button -->
        <transition name="cb-fab">
            <button v-if="!isOpen" class="chatbot__fab" @click="open" aria-label="Open HR assistant">
                <i class="fas fa-comments"></i>
                <span v-if="unread" class="chatbot__badge">{{ unread }}</span>
            </button>
        </transition>

        <!-- Chat Panel -->
        <transition name="cb-panel">
            <div v-if="isOpen" class="chatbot__panel">
                <!-- Header -->
                <div class="chatbot__header">
                    <div class="chatbot__header-left">
                        <div class="chatbot__avatar">
                            <i class="fas fa-robot"></i>
                        </div>
                        <div>
                            <div class="chatbot__title">HR Assistant</div>
                            <div class="chatbot__status">
                                <span class="chatbot__status-dot"></span>
                                Online
                            </div>
                        </div>
                    </div>
                    <button class="chatbot__close" @click="close" aria-label="Close chat">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <!-- Messages -->
                <div class="chatbot__messages" ref="messages">
                    <div v-for="(msg, idx) in messages" :key="idx" :class="['chatbot__msg', `chatbot__msg--${msg.from}`]">
                        <!-- Bot avatar -->
                        <div v-if="msg.from === 'bot'" class="chatbot__msg-avatar">
                            <i class="fas fa-robot"></i>
                        </div>

                        <div :class="['chatbot__bubble', `chatbot__bubble--${msg.from}`]">
                            <!-- Text message -->
                            <div v-if="msg.response && msg.response.type === 'text'" class="chatbot__text" v-html="renderMarkdown(msg.response.text)"></div>

                            <!-- Profile card -->
                            <div v-else-if="msg.response && msg.response.type === 'profile'">
                                <p class="chatbot__text">{{ msg.response.text }}</p>
                                <div class="chatbot__profile-card">
                                    <div v-for="field in msg.response.data" :key="field.label" class="chatbot__field">
                                        <span class="chatbot__field-label">{{ field.label }}</span>
                                        <span class="chatbot__field-value">{{ field.value }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Attendance overview -->
                            <div v-else-if="msg.response && msg.response.type === 'attendance'">
                                <p class="chatbot__text" v-html="renderMarkdown(msg.response.text)"></p>
                                <div class="chatbot__att-stats">
                                    <div v-for="s in msg.response.stats" :key="s.label" class="chatbot__att-stat">
                                        <span class="chatbot__att-stat-val">{{ s.value }}</span>
                                        <span class="chatbot__att-stat-lbl">{{ s.label }}</span>
                                    </div>
                                </div>
                                <div v-if="msg.response.present && msg.response.present.length" class="chatbot__att-section">
                                    <div class="chatbot__att-heading chatbot__att-heading--present">
                                        <i class="fas fa-check-circle"></i> Present ({{ msg.response.presentTotal }})
                                    </div>
                                    <div class="chatbot__att-names">
                                        <span v-for="name in msg.response.present" :key="name" class="chatbot__att-name chatbot__att-name--present">{{ name }}</span>
                                        <span v-if="msg.response.presentTotal > 10" class="chatbot__att-more">+{{ msg.response.presentTotal - 10 }} more</span>
                                    </div>
                                </div>
                                <div v-if="msg.response.absent && msg.response.absent.length" class="chatbot__att-section">
                                    <div class="chatbot__att-heading chatbot__att-heading--absent">
                                        <i class="fas fa-times-circle"></i> Absent ({{ msg.response.absentTotal }})
                                    </div>
                                    <div class="chatbot__att-names">
                                        <span v-for="name in msg.response.absent" :key="name" class="chatbot__att-name chatbot__att-name--absent">{{ name }}</span>
                                        <span v-if="msg.response.absentTotal > 10" class="chatbot__att-more">+{{ msg.response.absentTotal - 10 }} more</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Upcoming Birthdays -->
                            <div v-else-if="msg.response && msg.response.type === 'birthdays'">
                                <p class="chatbot__text" v-html="renderMarkdown(msg.response.text)"></p>
                                <div class="chatbot__bday-list">
                                    <div v-for="b in msg.response.data" :key="b.name" :class="['chatbot__bday-item', { 'chatbot__bday-item--today': b.isToday }]">
                                        <div class="chatbot__bday-icon">🎂</div>
                                        <div class="chatbot__bday-info">
                                            <span class="chatbot__bday-name">{{ b.name }}</span>
                                            <span class="chatbot__bday-date">{{ b.date }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Company & Departments combined -->
                            <div v-else-if="msg.response && msg.response.type === 'company_departments'">
                                <p class="chatbot__text" v-html="renderMarkdown(msg.response.text)"></p>
                                <div class="chatbot__cd-section">
                                    <div class="chatbot__cd-heading"><i class="fas fa-building"></i> Companies ({{ msg.response.companyCount }})</div>
                                    <div class="chatbot__table">
                                        <div v-for="row in msg.response.companies" :key="'c-'+row.label" class="chatbot__table-row">
                                            <span class="chatbot__table-label">{{ row.label }}</span>
                                            <span class="chatbot__table-value">{{ row.value }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="chatbot__cd-section">
                                    <div class="chatbot__cd-heading"><i class="fas fa-sitemap"></i> Departments ({{ msg.response.deptCount }})</div>
                                    <div class="chatbot__table">
                                        <div v-for="row in msg.response.departments" :key="'d-'+row.label" class="chatbot__table-row">
                                            <span class="chatbot__table-label">{{ row.label }}</span>
                                            <span class="chatbot__table-value">{{ row.value }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Table / list display -->
                            <div v-else-if="msg.response && msg.response.type === 'table'">
                                <p class="chatbot__text" v-html="renderMarkdown(msg.response.text)"></p>
                                <div class="chatbot__table">
                                    <div v-for="row in msg.response.data" :key="row.label" class="chatbot__table-row">
                                        <span class="chatbot__table-label">{{ row.label }}</span>
                                        <span class="chatbot__table-value">{{ row.value }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Help card -->
                            <div v-else-if="msg.response && msg.response.type === 'help'">
                                <p class="chatbot__text">{{ msg.response.text }}</p>
                                <div class="chatbot__help-grid">
                                    <div v-for="item in msg.response.data" :key="item.label" class="chatbot__help-item" @click="sendQuery(item.label)">
                                        <i :class="item.icon" class="chatbot__help-icon"></i>
                                        <div>
                                            <div class="chatbot__help-label">{{ item.label }}</div>
                                            <div class="chatbot__help-desc">{{ item.desc }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Error -->
                            <div v-else-if="msg.response && msg.response.type === 'error'" class="chatbot__text chatbot__text--error" v-html="renderMarkdown(msg.response.text)"></div>

                            <!-- Plain user message -->
                            <div v-else class="chatbot__text">{{ msg.text }}</div>

                            <!-- Inline actions / follow-up chips -->
                            <div v-if="msg.response && msg.response.actions && msg.response.actions.length" class="chatbot__actions">
                                <button v-for="act in msg.response.actions" :key="act.label" class="chatbot__action-btn" @click="act.query ? sendQuery(act.query) : goTo(act.url)">
                                    {{ act.label }}
                                </button>
                            </div>

                            <div class="chatbot__time">{{ msg.time }}</div>
                        </div>
                    </div>

                    <!-- Typing indicator -->
                    <div v-if="typing" class="chatbot__msg chatbot__msg--bot">
                        <div class="chatbot__msg-avatar"><i class="fas fa-robot"></i></div>
                        <div class="chatbot__bubble chatbot__bubble--bot chatbot__typing">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                </div>

                <!-- Suggestion chips (show only when few messages) -->
                <div v-if="messages.length <= 2" class="chatbot__suggestions">
                    <button v-for="s in suggestions" :key="s.label" class="chatbot__chip" @click="sendQuery(s.query)">
                        {{ s.label }}
                    </button>
                </div>

                <!-- Input -->
                <div class="chatbot__input-area">
                    <input ref="input" v-model="userInput" class="chatbot__input" placeholder="Ask me anything…" @keydown.enter="send" :disabled="typing" />
                    <button class="chatbot__send" @click="send" :disabled="!userInput.trim() || typing" aria-label="Send">
                        <i class="fas fa-paper-plane"></i>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { processMessage, getSuggestions } from "@/utils/chatbot"

export default {
    name: "ChatBot",
    data() {
        return {
            isOpen: false,
            userInput: "",
            messages: [],
            typing: false,
            unread: 0,
            suggestions: getSuggestions(),
        }
    },
    created() {
        this.loadHistory()
    },
    methods: {
        open() {
            this.isOpen = true
            this.unread = 0
            if (!this.messages.length) {
                this.addBotMessage({ text: "Hi! 👋 I'm your HR assistant. Ask me anything about your profile, leaves, salary, attendance, or tasks.", type: "text" })
            }
            this.$nextTick(() => {
                this.$refs.input?.focus()
            })
        },
        close() {
            this.isOpen = false
        },
        async send() {
            const text = this.userInput.trim()
            if (!text || this.typing) return

            this.userInput = ""
            this.messages.push({ from: "user", text, time: this.now() })
            this.saveHistory()
            this.scrollDown()

            this.typing = true
            try {
                const response = await processMessage(text)
                // Simulate a small delay for natural feel
                await this.delay(400 + Math.random() * 400)
                this.addBotMessage(response)
            } catch {
                this.addBotMessage({ text: "Something went wrong. Please try again.", type: "error" })
            }
            this.typing = false
            this.scrollDown()
        },
        sendQuery(query) {
            this.userInput = query
            this.send()
        },
        goTo(url) {
            if (url) {
                this.$router.push(url).catch(() => {})
                this.close()
            }
        },
        addBotMessage(response) {
            this.messages.push({ from: "bot", response, time: this.now() })
            if (!this.isOpen) this.unread++
            this.saveHistory()
            this.scrollDown()
        },
        now() {
            return new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
        },
        scrollDown() {
            this.$nextTick(() => {
                const el = this.$refs.messages
                if (el) el.scrollTop = el.scrollHeight
            })
        },
        delay(ms) {
            return new Promise(r => setTimeout(r, ms))
        },
        renderMarkdown(text) {
            if (!text) return ""
            // Very minimal markdown → HTML: bold, links, newlines
            return text
                .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="chatbot__link">$1</a>')
                .replace(/\n/g, "<br>")
        },
        saveHistory() {
            try {
                // Keep last 50 messages to avoid bloating storage
                const toSave = this.messages.slice(-50)
                localStorage.setItem("chatbot_history", JSON.stringify(toSave))
            } catch { /* quota exceeded — ignore */ }
        },
        loadHistory() {
            try {
                const saved = localStorage.getItem("chatbot_history")
                if (saved) {
                    this.messages = JSON.parse(saved)
                }
            } catch { /* corrupted data — ignore */ }
        },
    },
}
</script>

<style lang="scss" scoped>
// ────── Design Tokens (inline to keep component self-contained) ──────
$primary: #005e89;
$primaryLight: #e8f4fa;
$primaryDark: #003d5c;
$gray50: #f8fafc;
$gray100: #f1f5f9;
$gray200: #e2e8f0;
$gray300: #cbd5e1;
$gray400: #94a3b8;
$gray500: #64748b;
$gray700: #334155;
$gray800: #1e293b;
$white: #ffffff;
$danger: #ef4444;
$radiusSm: 8px;
$radiusMd: 12px;
$radiusLg: 16px;
$shadowLg: 0 10px 15px -3px rgba(0,0,0,.08), 0 4px 6px -4px rgba(0,0,0,.04);
$shadowXl: 0 20px 25px -5px rgba(0,0,0,.1), 0 8px 10px -6px rgba(0,0,0,.06);

.chatbot {
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 9999;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    text-align: left;
}

// ──── FAB ────
.chatbot__fab {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, $primary, $primaryDark);
    border: none;
    color: $white;
    font-size: 22px;
    cursor: pointer;
    box-shadow: $shadowLg, 0 0 0 0 rgba($primary, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    animation: fabPulse 3s ease-in-out infinite;

    &:hover {
        transform: scale(1.08);
        box-shadow: $shadowXl;
        animation: none;
    }
    &:active {
        transform: scale(0.95);
    }
}

@keyframes fabPulse {
    0%, 100% { box-shadow: $shadowLg, 0 0 0 0 rgba($primary, 0.35); }
    50% { box-shadow: $shadowLg, 0 0 0 10px rgba($primary, 0); }
}

.chatbot__badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: $danger;
    color: $white;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid $white;
}

// ──── Panel ────
.chatbot__panel {
    width: 380px;
    max-width: calc(100vw - 32px);
    height: 560px;
    max-height: calc(100vh - 100px);
    border-radius: $radiusLg;
    background: $white;
    box-shadow: $shadowXl;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid $gray200;
}

// ──── Header ────
.chatbot__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: linear-gradient(135deg, $primary, $primaryDark);
    color: $white;
    flex-shrink: 0;
}
.chatbot__header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}
.chatbot__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}
.chatbot__title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
}
.chatbot__status {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    opacity: 0.85;
}
.chatbot__status-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #34d399;
    display: inline-block;
    animation: statusPulse 2s infinite;
}
@keyframes statusPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}
.chatbot__close {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(255,255,255,0.15);
    color: $white;
    border-radius: 50%;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    &:hover { background: rgba(255,255,255,0.3); }
}

// ──── Messages area ────
.chatbot__messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: $gray50;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: $gray300; border-radius: 4px; }
}

.chatbot__msg {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    max-width: 88%;

    &--user {
        align-self: flex-end;
        flex-direction: row-reverse;
    }
    &--bot {
        align-self: flex-start;
    }
}

.chatbot__msg-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: $primaryLight;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
}

.chatbot__bubble {
    padding: 10px 14px;
    border-radius: $radiusMd;
    font-size: 13px;
    line-height: 1.55;
    position: relative;

    &--user {
        background: $primary;
        color: $white;
        border-bottom-right-radius: 4px;
    }
    &--bot {
        background: $white;
        color: $gray700;
        border: 1px solid $gray200;
        border-bottom-left-radius: 4px;
    }
}

.chatbot__text {
    margin: 0;
    word-break: break-word;
}
.chatbot__text--error {
    color: $danger;
}

.chatbot__time {
    font-size: 10px;
    color: $gray400;
    margin-top: 4px;
    text-align: right;
}
.chatbot__bubble--user .chatbot__time {
    color: rgba(255,255,255,0.6);
}

// ──── Profile card ────
.chatbot__profile-card {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.chatbot__field {
    display: flex;
    justify-content: space-between;
    padding: 5px 8px;
    background: $gray50;
    border-radius: 6px;
    font-size: 12px;
}
.chatbot__field-label {
    color: $gray500;
    font-weight: 500;
}
.chatbot__field-value {
    color: $gray700;
    font-weight: 600;
    text-align: right;
    max-width: 60%;
    word-break: break-word;
}

// ──── Table display ────
.chatbot__table {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

// ──── Company & Department combined ────
.chatbot__cd-section {
    margin-top: 10px;
    &:first-of-type { margin-top: 8px; }
}
.chatbot__cd-heading {
    font-size: 12px;
    font-weight: 600;
    color: $primary;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 5px;
    i { font-size: 11px; }
}

.chatbot__table-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 8px;
    background: $gray50;
    border-radius: 6px;
    font-size: 12px;
}
.chatbot__table-label {
    color: $gray700;
    font-weight: 500;
}
.chatbot__table-value {
    color: $primary;
    font-weight: 600;
    text-align: right;
}

// ──── Help grid ────
.chatbot__help-grid {
    margin-top: 8px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;
}
.chatbot__help-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 8px;
    background: $gray50;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: $primaryLight; }
}
.chatbot__help-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: $primaryLight;
    color: $primary;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
}
.chatbot__help-label {
    font-size: 12px;
    font-weight: 600;
    color: $gray700;
    line-height: 1.2;
}
.chatbot__help-desc {
    font-size: 11px;
    color: $gray500;
    line-height: 1.2;
}

// ──── Attendance overview ────
.chatbot__att-stats {
    display: flex;
    gap: 6px;
    margin-top: 8px;
}
.chatbot__att-stat {
    flex: 1;
    text-align: center;
    padding: 8px 4px;
    background: $gray50;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.chatbot__att-stat-val {
    font-size: 18px;
    font-weight: 700;
    color: $primary;
    line-height: 1.2;
}
.chatbot__att-stat-lbl {
    font-size: 10px;
    font-weight: 500;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.chatbot__att-section {
    margin-top: 8px;
}
.chatbot__att-heading {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    &--present { color: #10b981; }
    &--absent { color: #ef4444; }
}
.chatbot__att-names {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.chatbot__att-name {
    font-size: 11px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 999px;
    &--present {
        background: rgba(16, 185, 129, 0.1);
        color: #059669;
    }
    &--absent {
        background: rgba(239, 68, 68, 0.1);
        color: #dc2626;
    }
}
.chatbot__att-more {
    font-size: 11px;
    color: $gray500;
    padding: 3px 8px;
    font-style: italic;
}

// ──── Birthday list ────
.chatbot__bday-list {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.chatbot__bday-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: $gray50;
    border-radius: 8px;
    transition: background 0.15s;
    &--today {
        background: #fef3c7;
        border: 1px solid #fbbf24;
    }
}
.chatbot__bday-icon {
    font-size: 18px;
    flex-shrink: 0;
}
.chatbot__bday-info {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
}
.chatbot__bday-name {
    font-size: 12px;
    font-weight: 600;
    color: $gray700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.chatbot__bday-date {
    font-size: 11px;
    color: $gray500;
    font-weight: 500;
}
.chatbot__bday-item--today .chatbot__bday-date {
    color: #b45309;
    font-weight: 600;
}

// ──── Inline action chips ────
.chatbot__actions {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.chatbot__action-btn {
    font-size: 11px;
    font-weight: 500;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid $primary;
    background: $white;
    color: $primary;
    cursor: pointer;
    transition: all 0.15s;
    &:hover {
        background: $primary;
        color: $white;
    }
}

// ──── Typing indicator ────
.chatbot__typing {
    display: flex;
    gap: 4px;
    padding: 12px 16px !important;
    span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: $gray400;
        animation: typingBounce 1.4s infinite ease-in-out;
        &:nth-child(2) { animation-delay: 0.15s; }
        &:nth-child(3) { animation-delay: 0.3s; }
    }
}
@keyframes typingBounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
    40% { transform: translateY(-5px); opacity: 1; }
}

// ──── Suggestion chips (bottom area) ────
.chatbot__suggestions {
    padding: 0 16px 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    background: $gray50;
    border-top: 1px solid $gray200;
}
.chatbot__chip {
    font-size: 11px;
    font-weight: 500;
    padding: 5px 12px;
    border-radius: 999px;
    border: 1px solid $gray300;
    background: $white;
    color: $gray700;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    &:hover {
        border-color: $primary;
        color: $primary;
        background: $primaryLight;
    }
}

// ──── Input area ────
.chatbot__input-area {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid $gray200;
    background: $white;
    flex-shrink: 0;
}
.chatbot__input {
    flex: 1;
    border: 1px solid $gray200;
    border-radius: 999px;
    padding: 9px 16px;
    font-size: 13px;
    color: $gray700;
    font-family: inherit;
    outline: none;
    transition: border-color 0.2s;
    &::placeholder { color: $gray400; }
    &:focus { border-color: $primary; }
}
.chatbot__send {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: $primary;
    color: $white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: all 0.15s;
    flex-shrink: 0;
    &:hover:not(:disabled) { background: $primaryDark; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// ──── Links inside bot bubbles ────
.chatbot__link {
    color: $primary !important;
    font-weight: 600;
    text-decoration: underline !important;
    &:hover { color: $primaryDark !important; }
}

// ──── Transitions ────
.cb-fab-enter-active, .cb-fab-leave-active {
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.cb-fab-enter, .cb-fab-leave-to {
    opacity: 0;
    transform: scale(0.6);
}

.cb-panel-enter-active {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.cb-panel-leave-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.cb-panel-enter, .cb-panel-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
}

// ──── Mobile ────
@media (max-width: 480px) {
    .chatbot {
        bottom: 16px;
        right: 16px;
    }
    .chatbot__panel {
        width: calc(100vw - 32px);
        height: calc(100vh - 100px);
        position: fixed;
        bottom: 16px;
        right: 16px;
    }
    .chatbot__fab {
        width: 50px;
        height: 50px;
        font-size: 20px;
    }
}
</style>
